import React, { ReactElement } from 'react';
import { useInView } from 'react-intersection-observer';
import { useRouter } from 'next/router';
import { AnimatePresence } from 'motion/react';
import { Text } from '@feast-it/ui/light';

import { trackEvent } from '../../modules/analytics';
import { getAbsoluteUrl } from '@/helpers/formatUrl';
import { GET_QUOTES_CLICKED } from '../../constants/analytics';

import {
  Container,
  Content,
  StyledText,
  StyledButton,
  IntersectionObserver,
} from './StickyCTA.styled';

import { getTransitionDuration } from '../../utils/getTransitionDuration';

export type StickyCTAVariant = 'elevated' | 'full';

export interface StickyCTAProps {
  heading: string;
  buttonText: string;
  buttonLink: string;
  text?: string;
  variant?: StickyCTAVariant;
}

export function StickyCTA({
  heading,
  buttonText,
  buttonLink,
  text,
  variant = 'elevated',
}: StickyCTAProps): ReactElement {
  const router = useRouter();
  const [ref, isAboveFold] = useInView({
    threshold: 0,
  });

  return (
    <>
      <IntersectionObserver ref={ref} />

      <AnimatePresence>
        {!isAboveFold && (
          <Container
            key="sticky-cta"
            variant={variant}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              duration: getTransitionDuration('medium'),
              ease: 'easeOut',
            }}
          >
            <Content variant={variant}>
              <StyledText>
                <Text as="h2" style="bodyBoldMedium" color="black">
                  {heading}
                </Text>

                {text && (
                  <Text style="bodySmall" color="darkGrey">
                    {text}
                  </Text>
                )}
              </StyledText>

              <StyledButton
                href={buttonLink}
                onClick={() =>
                  trackEvent(GET_QUOTES_CLICKED, {
                    source: 'sticky bottom',
                    currentPage: getAbsoluteUrl(router.asPath),
                    destinationUrl: getAbsoluteUrl(buttonLink),
                  })
                }
                size="large"
                color="mediumBlue"
                variant="solid"
              >
                {buttonText}
              </StyledButton>
            </Content>
          </Container>
        )}
      </AnimatePresence>
    </>
  );
}
